import React, { useState, useEffect, useRef } from "react";
import { Heading, ExternalLink } from "../../../components";
import { Formik, Form, Field } from "formik";
import ErrorIcon from "../../../img/error-icon.svg";
import Tick from "../../../img/tick.svg";
import { trackFormEvent, trackCtaClick } from "../../../utilities/tracking";
import * as Yup from "yup";

const FORM_NAME = "hcp_contact";

// CRA only exposes env vars prefixed with REACT_APP_. Fall back to the live API
// so a missing/empty .env never breaks submissions in production.
const API_URL = process.env.REACT_APP_API_URL || "https://api.bunov.co.uk";

const formSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Field is required"),
  email: Yup.string().email("Invalid email").required("Field is required"),
  message: Yup.string().required("Field is required"),
});

export default function ContactUs() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [token, setToken] = useState();
  const [submitError, setSubmitError] = useState(null);

  // Form-abandonment tracking state. Refs (not state) so the values are stable
  // inside the unmount/unload handlers and never trigger re-renders.
  const formState = useRef({
    started: false,
    submitted: false,
    lastField: null,
    completed: new Set(),
  });

  useEffect(() => {
    document.title = "Bunov® - Contact us";

    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?render=6LcymdcfAAAAABLLLKH6IgQaohfHFLTomAYfdCqQ";
    script.addEventListener("load", handleLoaded);
    document.body.appendChild(script);
  }, []);

  // Fire `form_abandon` if the user started but never submitted, on both
  // navigation away (unmount) and browser/tab close (beforeunload).
  useEffect(() => {
    const reportAbandon = () => {
      const s = formState.current;
      if (s.started && !s.submitted) {
        trackFormEvent("form_abandon", FORM_NAME, {
          last_field: s.lastField,
          fields_completed: s.completed.size,
        });
        s.started = false; // guard against double-firing
      }
    };
    console.log("api url", API_URL);
    window.addEventListener("beforeunload", reportAbandon);
    return () => {
      window.removeEventListener("beforeunload", reportAbandon);
      reportAbandon();
    };
  }, []);

  const handleLoaded = (_) => {
    window.grecaptcha.ready((_) => {
      window.grecaptcha
        .execute("6LcymdcfAAAAABLLLKH6IgQaohfHFLTomAYfdCqQ", {
          action: "contact",
        })
        .then((token) => {
          setToken(token);
        });
    });
  };

  const handleFieldFocus = (name) => {
    if (!formState.current.started) {
      formState.current.started = true;
      trackFormEvent("form_start", FORM_NAME);
    }
    formState.current.lastField = name;
  };

  const handleFieldBlur = (e, formikHandleBlur) => {
    const { name, value } = e.target;
    if (value && value.trim()) {
      formState.current.completed.add(name);
    } else {
      formState.current.completed.delete(name);
    }
    formikHandleBlur(e);
  };

  return (
    <main className="contact">
      <Heading title="Contact us" />
      <section className="section section--grey section--full-width section--contact">
        <div className="resource-container">
          {!isSubmitted ? (
            <div
              className="recaptcha"
              data-sitekey="6LcymdcfAAAAABLLLKH6IgQaohfHFLTomAYfdCqQ_"
              data-size="invisible"
            >
              <Formik
                initialValues={{
                  name: "",
                  email: "",
                  message: "",
                }}
                validationSchema={formSchema}
                onSubmit={async (values) => {
                  setIsSubmitting(true);
                  setSubmitError(null);
                  formState.current.submitted = true;
                  trackFormEvent("form_submit", FORM_NAME);
                  const endpoint = `${API_URL}/api/email`;
                  trackCtaClick({
                    text: "Submit",
                    destination: endpoint,
                    location: "contact-form-submit",
                  });
                  values.token = token;
                  const requestOptions = {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(values),
                  };

                  // Abort a stuck request so the button can't spin forever if the
                  // server accepts the connection but never responds.
                  const controller = new AbortController();
                  const timeout = setTimeout(() => controller.abort(), 20000);

                  try {
                    console.log("[contact form] POST", endpoint);
                    const response = await fetch(endpoint, {
                      ...requestOptions,
                      signal: controller.signal,
                    });
                    clearTimeout(timeout);
                    console.log("[contact form] response", response.status, response.statusText);
                    // Guard against non-JSON error responses (e.g. HTML error pages).
                    const data = await response.json().catch(() => null);

                    if (response.ok && data && data.response === "Success") {
                      trackFormEvent("form_submit_success", FORM_NAME);
                      setIsSubmitted(true);
                    } else {
                      console.error("[contact form] submission failed", {
                        endpoint,
                        status: response.status,
                        statusText: response.statusText,
                        body: data,
                      });
                      trackFormEvent("form_submit_error", FORM_NAME, {
                        status: response.status,
                      });
                      setSubmitError(
                        "Sorry, something went wrong sending your enquiry. Please try again, or email us directly."
                      );
                      setIsSubmitting(false);
                    }
                  } catch (err) {
                    // Network failure, CORS block, DNS error, or timeout (abort).
                    clearTimeout(timeout);
                    const timedOut = err && err.name === "AbortError";
                    console.error("[contact form] request could not complete", {
                      endpoint,
                      timedOut,
                      error: err,
                    });
                    trackFormEvent("form_submit_error", FORM_NAME, {
                      error: String(err),
                      timed_out: timedOut,
                    });
                    setSubmitError(
                      timedOut
                        ? "The request timed out. Please try again in a moment."
                        : "Sorry, we couldn't reach the server. Please check your connection and try again."
                    );
                    setIsSubmitting(false);
                  }
                }}
              >
                {({ errors, touched, handleBlur }) => (
                  <Form>
                    <div className="form-top">
                      <div className="field">
                        <label htmlFor="name">Full name</label>
                        <Field
                          id="name"
                          name="name"
                          autoComplete="name"
                          onFocus={() => handleFieldFocus("name")}
                          onBlur={(e) => handleFieldBlur(e, handleBlur)}
                          className={errors.name && touched.name ? "error" : ""}
                        />
                        <img src={ErrorIcon} alt="" />
                        <div
                          className={
                            errors.name && touched.name ? "error-message" : "error-message hide"
                          }
                        >
                          {errors.name || " "}
                        </div>
                      </div>
                      <div className="field">
                        <label htmlFor="email">Email address</label>
                        <Field
                          id="email"
                          name="email"
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          onFocus={() => handleFieldFocus("email")}
                          onBlur={(e) => handleFieldBlur(e, handleBlur)}
                          className={errors.email && touched.email ? "error" : ""}
                        />
                        <img src={ErrorIcon} alt="" />
                        <div
                          className={
                            errors.email && touched.email ? "error-message" : "error-message hide"
                          }
                        >
                          {errors.email || " "}
                        </div>
                      </div>
                    </div>
                    <div className="form-bottom">
                      <div className="field">
                        <label htmlFor="message">Your enquiry / message</label>
                        <Field
                          id="message"
                          name="message"
                          as="textarea"
                          onFocus={() => handleFieldFocus("message")}
                          onBlur={(e) => handleFieldBlur(e, handleBlur)}
                          className={errors.message && touched.message ? "error" : ""}
                        />
                        <div
                          className={
                            errors.message && touched.message
                              ? "error-message"
                              : "error-message hide"
                          }
                        >
                          {errors.message || " "}
                        </div>
                      </div>
                      <p>
                        Your details will be processed in accordance with our Privacy Policy
                        available at{" "}
                        <ExternalLink
                          link="https://glenmarkpharma.com/privacy-portal/"
                          title="glenmarkpharma.co.uk/privacy-policy."
                        />{" "}
                      </p>
                      {submitError && (
                        <p className="submit-error" role="alert">
                          {submitError}
                        </p>
                      )}
                      <div className="submit-wrapper">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn btn--primary form-submission-attempt"
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          ) : (
            <div className="thank-you">
              <div className="icon">
                <img src={Tick} alt="" />
              </div>
              <p className="bold">Your enquiry has been sent</p>
              <p>A Glenmark Pharmaceuticals representative will be in touch shortly.</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
